import * as $ from 'jquery';
import { disposableSubscription, compositeSubscription, disposable, Lazy } from 'common/barrels/core';
import { Observable, BehaviorSubject } from 'common/barrels/rx';
import { Seq } from 'immutable';
import * as rxui from 'common/rxui/core';
import { camel } from 'common/functions/string';
import { unobtrusiveScroll } from 'common/rxui/scroll';

import 'common/styles/rxui/core.less';
import 'common/styles/rxui/rxtable.less';

var testNode = document.createElement('span');
var applyText = testNode.textContent !== undefined
    ? (x, y) => x.textContent = y
    : (x, y) => x.innerText = y;
testNode = null;

export class ColumnInfo {
    constructor(public name: string, public displayName: string, public size: number, public sizeType: string, public minAutoSize: number = 0, public maxAutoSize: number = 0) { }
}

export class ColumnResizeRequest {
    constructor(public index: number, public size: number) { }
}

export class ColumnReorderRequest {
    constructor(public oldIndex: number, public newIndex: number) { }
}

export class ColumnSortRequest {
    constructor(public index: number, public ascending: boolean) { }
}

export function makeHeadersDraggable($table: JQuery): Observable<ColumnReorderRequest> {
    var $ghost = null;
    var $header = null;
    var headerOffset = null;
    var $body = $('body');
    var $document = $(document);

    var mouseup = rxui.mouseupleft($document).share();

    var mousemove = rxui.mousemove($document).share();

    var dragstart = rxui.mousedownleft($table.find('> thead > tr > th > div'))
        .filter(x => !$(x.target).hasClass('rxui-table-resize-handle'))
        .filter(x => !$(x.target).hasClass('rxui-table-filler'))
        .switchMap(x => rxui.mousemove($document).filter(e => (<any>e).originalEvent.movementX !== 0).first().takeUntil(mouseup).map(() => x))
        .share();

    var dragging = dragstart.switchMap(() => mousemove.pairwise().takeUntil(mouseup)).share();

    var dragdrop = dragstart.switchMap(() => mouseup.first()).share();

    $table.addClass('rxui-table-draggableheaders');

    var disposables = disposableSubscription(
        dragstart.subscribe(x => {
            $header = $((<any>x).target).closest('th').children('div');
            headerOffset = $header.offset();
            $body.addClass('rxui-noselect');
            $ghost = $header.clone().children('.rxui-table-sort-caret').remove().end().width($header.parent().width()).height($header.height()).addClass('rxui-table-header-ghost rxui-noselect').css('left', $header[0].offsetLeft).appendTo($table.parent());
        }),

        dragging.subscribe(x => {
            var delta = x[1].pageX - x[0].pageX;
            var o = $ghost.offset();
            o.left += delta;
            $ghost.offset(o);
        }),

        disposable(() => $table.removeClass('rxui-table-draggableheaders'))
    );

    return Observable.using(() => compositeSubscription(disposables), () => dragdrop.map(() => {
        var condition = $e => {
            var ghostOffset = $ghost.offset();
            var edge = ghostOffset.left > headerOffset.left ? ghostOffset.left : ghostOffset.left + $ghost.width();
            return ($e.offset().left < edge) && ($e.offset().left + $e.parent().width() > edge);
        };

        var headerElements = jqToSeq($table.find('> thead > tr > th > div')).toIndexedSeq();
        var oldIndex = headerElements.findIndex($e => $e[0] === $header[0]);
        var newIndex = headerElements.findIndex($e => condition($e));

        $ghost.remove();
        $body.removeClass('rxui-noselect');

        return new ColumnReorderRequest(oldIndex, newIndex);
    }).filter(x => x.oldIndex !== x.newIndex && x.newIndex !== -1));
}

export function makeColumnsResizable($table: JQuery): Observable<ColumnResizeRequest> {
    var $indicator = null;
    var $thead = $table.children('thead');
    var $document = $(document);

    var makeHandleElement = (parentElement: JQuery) => $('<div />').addClass('rxui-table-resize-handle').appendTo(parentElement);

    var makeHandle = (element: JQuery, index: number) => $().add(makeHandleElement(element).css('right', 0));

    var $handles = jqToSeq($thead.find('> tr > th > div'))
        .butLast()
        .map((x, i) => ({ element: x, index: i }))
        .reduce((a, x) => a.add(makeHandle(x.element, x.index)), $());

    var mouseup = rxui.mouseupleft($document).share();
    var mousemove = rxui.mousemove($document).share();
    var dragstart = rxui.mousedownleft($handles)
        .do(e => e.preventDefault())
        .share();
    var dragging = dragstart.switchMap(() => mousemove.pairwise().takeUntil(mouseup)).share();
    var dragdrop = dragstart.switchMap(() => mouseup.first()).share();

    var disposables = disposableSubscription(
        dragstart.subscribe(x => {
            var $handle = $(x.target);
            var column = $handle.parent().parent().index();
            var $origin = $thead.find('> tr > th').eq(column);
            var initialPosition = $origin.position().left + $origin.width();
            $indicator = $('<div />').addClass('rxui-table-drag-indicator').css('left', `${initialPosition}px`).data('column', column).appendTo($table.parent());
            $('body').addClass('rxui-noselect');
        }),

        dragging.subscribe(x => {
            var delta = x[1].pageX - x[0].pageX;
            var o = $indicator.offset();
            o.left += delta;
            $indicator.offset(o);
        }),

        disposable(() => $handles.remove())
    );

    return Observable.using(() => compositeSubscription(disposables), () => dragdrop.map(() => {
        var column = $indicator.data('column');
        var size = $indicator.position().left - $table.find('> thead > tr > th').eq(column).position().left;
        $indicator.remove();
        $('body').removeClass('rxui-noselect');
        return new ColumnResizeRequest(column, size);
    }));
}

export function makeSortable($table: JQuery): Observable<ColumnSortRequest> {
    var ascendingClass = 'fa-caret-down';
    var descendingClass = 'fa-caret-up';
    var $thead = $table.children('thead');
    var $headers = $thead.find('> tr > th > div');
    var factory = (x: string): JQuery => $('<div />').addClass('rxui-table-sort-caret').addClass(`fa ${x}`);
    var getNewDirection = ($e: JQuery) => ($e.children().children(`.${ascendingClass}`).length > 0) ? 'descending' : 'ascending';

    $table.addClass('rxui-table-sortableheaders');

    return rxui.mousedownleft($headers)
        .filter(x => !$(x.target).hasClass('rxui-table-resize-handle'))
        .filter(x => !$(x.target).hasClass('rxui-table-filler'))
        .switchMap(() => rxui.mouseupleft($headers).takeUntil(rxui.mousemove($headers).filter(e => (<any>e).originalEvent.movementX !== 0)))
        .map(x => {
            var $e = $(x.target).closest('th');
            var i = $thead.find('> tr > th').index($e);
            var direction = getNewDirection($e);
            return ({ column: i, direction: direction, $e: $e });
        })
        .do(x => {
            $thead.find('> tr > th > div > .rxui-table-sort-caret').remove();
            factory(x.direction === 'ascending' ? ascendingClass : descendingClass).insertAfter(x.$e.find('> div > span'));
        })
        .map(x => new ColumnSortRequest(x.column, x.direction === 'ascending'))
        .share();
}

export function moveColumn($table: JQuery, oldIndex: number, newIndex: number, columnCount: number): void {
    // this prevents placing columns after the filler
    if (newIndex === columnCount)
        newIndex--;

    var move = oldIndex < newIndex
        ? (tr: JQuery) => tr.children().eq(oldIndex).insertAfter(tr.children().eq(newIndex))
        : (tr: JQuery) => tr.children().eq(oldIndex).insertBefore(tr.children().eq(newIndex));

    // reorder all existing cells (including headers)
    jqToSeq($table.children('thead, tbody').children('tr')).forEach(move);
    updateHeadingPositions($table);
}

function makeHeaderCell(meta: ColumnInfo): JQuery {
    var $th = $('<th />');
    var $outer = $('<div />').appendTo($th);
    $('<span />').text(meta.displayName).appendTo($outer);

    return $th;
}

function makeHeaderFillerCell(): JQuery {
    return $('<th />').append($('<div />').addClass('rxui-table-filler'));
}

export function buildHeader($table: JQuery, columnMetadata: ColumnInfo[]): JQuery {
    return $('<thead />')
        .addClass('rxui-noselect')
        .append(Seq(columnMetadata).reduce((a: JQuery, x) => a.append(makeHeaderCell(x)), $('<tr />')).append(makeHeaderFillerCell()))
        .appendTo($table);
}

export function createChock(): JQuery {
    return $('<caption />').html('&#160;');
}

function getCellValue(item: any, meta: ColumnInfo): any {
    var value = item[camel(meta.name)];

    return value === undefined || value === null ? '' : value;
}

export function populateTable(
    $table: JQuery,
    columnMetadata: ColumnInfo[],
    items: any[],
    keyLambda: (any) => any,
    sort: ColumnSortRequest,
    rowFactory: (item: any, metas: ColumnInfo[], keyLambda: (x: any) => any) => HTMLTableRowElement = null
): void {
    var seqItems = Seq(items);

    var meta = new Lazy(() => columnMetadata[sort.index]);

    var sortedItems = !sort ? seqItems
        : sort.ascending ? seqItems.sortBy(x => getCellValue(x, meta.value()))
        : seqItems.sortBy(x => getCellValue(x, meta.value())).reverse();

    // the below code has been tuned for speed; it was unacceptably slow running in IE8 mode
    var metas = columnMetadata;
    var frag = document.createDocumentFragment();
    var sitems = sortedItems.toArray();

    if (rowFactory) {
        for (var i = 0; i < sitems.length; i++)
            frag.appendChild(rowFactory(sitems[i], metas, keyLambda));
    }
    else {
        for (var i = 0; i < sitems.length; i++)
            frag.appendChild(optimizedRowConstruction(sitems[i], metas, keyLambda));
    }

    var $tbody = $('tbody', $table);
    $tbody.empty();
    $tbody[0].appendChild(frag);

    applyAlternatingRowStyles($table);
}

export function optimizedRowConstruction(item: any, metas: ColumnInfo[], keyLambda: (any) => any): HTMLTableRowElement {
    var row = document.createElement('tr');
    $(row).data('key', keyLambda(item));
    for (var j = 0; j < metas.length; j++) {
        var cell = document.createElement('td');
        var div = document.createElement('div');

        cell.appendChild(div);

        var v = getCellValue(item, metas[j]);
        if (v instanceof $) {
            div.appendChild(v[0]);
        }
        else {
            var span = document.createElement('span');
            applyText(span, v);
            div.appendChild(span);
        }

        row.appendChild(cell);
    }

    var filler = document.createElement('td');
    filler.className = 'rxui-table-filler';
    row.appendChild(filler);

    return row;
}

export function applyAlternatingRowStyles($table: JQuery) {
    $table.find('> tbody > tr:odd').removeClass('rxui-table-row-even').addClass('rxui-table-row-odd');
    $table.find('> tbody > tr:even').removeClass('rxui-table-row-odd').addClass('rxui-table-row-even');
}

function getHeaderContentWidth($thead: JQuery, index: number): number {
    return jqToSeq($thead.find('> tr > th').eq(index).children('div').children('span')).map(x => Math.ceil(x.width())).max();
}

// imperative and brittle for speed
function getMaxCellContentWidth(rows: HTMLTableRowElement[], index: number): number {
    var max = 0;
    for (var i = 0; i < rows.length; i++) {
        if (rows[i].className.indexOf('rxui-table-nosize') === -1) {
            max = Math.max(max, Math.ceil((<HTMLSpanElement>rows[i].childNodes[index].childNodes[0].childNodes[0]).offsetWidth));
        }
    }

    return max;
}

function getColumnContentWidth($thead: JQuery, $tbody: JQuery, sizeType: string, index: number, size: number, minSize: number, maxSize: number, autoClearance: number, rows: HTMLTableRowElement[]): number {
    return sizeType === 'sizeToHeader' ? applyLimits(getHeaderContentWidth($thead, index) + autoClearance, minSize, maxSize)
        : sizeType === 'sizeToCells' ? applyLimits(getMaxCellContentWidth(rows, index) + autoClearance, minSize, maxSize)
            : sizeType === 'auto' ? applyLimits(Math.max(getMaxCellContentWidth(rows, index), getHeaderContentWidth($thead, index)) + autoClearance, minSize, maxSize)
                : size;
}

function applyLimits(value: number, min: number, max: number) {
    var minApplied = min ? Math.max(min, value) : value;
    return max ? Math.min(max, minApplied) : minApplied;
}

function applyColumnWidths($table: JQuery, columnMetadata: { sizeType: string; size: number; minAutoSize: number; maxAutoSize: number }[], minColumnWidth: number, autoClearance: number): void {
    var $thead = $table.children('thead');
    var $tbody = $table.children('tbody');

    var rawRows = $tbody.children('tr').toArray().map(x => <HTMLTableRowElement>x);

    var numbered = Seq(columnMetadata).map((x, i) => ({ index: i, meta: x }));

    var nonStar = Seq(numbered
        .filter(x => x.meta.sizeType !== 'star')
        .map(x => ({ index: x.index, size: Math.max(getColumnContentWidth($thead, $tbody, x.meta.sizeType, x.index, x.meta.size, x.meta.minAutoSize, x.meta.maxAutoSize, autoClearance, rawRows), minColumnWidth) }))
        .toArray());

    var availableSpace = $table.width();

    var starSpace = availableSpace - nonStar.map(x => x.size).reduce((a, x) => a + x, 0);
    var rawStar = numbered.filter(x => x.meta.sizeType === 'star').map(x => ({ index: x.index, stars: x.meta.size }));
    var starTotal = rawStar.map(x => x.stars).reduce((a, x) => a + x, 0);
    var star = rawStar.map(x => ({ index: x.index, size: Math.max(starSpace * x.stars / starTotal, minColumnWidth) }));

    var $th = $thead.children('tr').children('th');
    nonStar.forEach(x => $th.eq(x.index).width(x.size).css('min-width', x.size));
    star.forEach(x => $th.eq(x.index).width(x.size));

    $table.css('min-width', nonStar.reduce((a, x) => a + x.size, 0) + (star.count() * minColumnWidth));
}

export function updateHeadingPositions($table: JQuery): void {
    $table.find('thead>tr>th>div').toArray().map(e => $(e)).forEach($e => $e.css('left', $e.parent().offset().left - $e.parent().offsetParent().offset().left));
}

export function resizeInternal($table: JQuery): void {
    var isChrome = !!(<any>window).chrome

    // voodoo for Chrome
    if (isChrome) {
        $table.css('display', 'none');
        // ReSharper disable once WrongExpressionStatement
        $table[0].offsetHeight;
        $table.css('display', '');
    }

    var areSortable = $table.hasClass('rxui-table-sortableheaders');

    // more voodoo for Chrome... it mismeasures things on every other pass... the underlying cause hasn't been identified (styles? pixel interpolation?)
    // the simple fix is saying if it's just trying to grow one pixel from its current size, skip the sizing. This shouldn't really rob anything of space or lead
    // to any undesired layout because there's enough slack in the padding that nothing will be clipped
    var setDivToHeaderWidth = !isChrome
        ? $th => $th.children('div').width($th.width() - (areSortable ? 10 : 0))
        : $th => {
            var $div = $th.children('div');
            var existingWidth = $div.width();
            var newWidth = $th.width() - (areSortable ? 10 : 0);

            if (newWidth !== (existingWidth + 1))
                $div.width(newWidth);
        };

    jqToSeq($table.find('> thead > tr > th')).map(x => $(x)).forEach(setDivToHeaderWidth);
}

export function resize($table: JQuery, columnMetadata: { sizeType: string; size: number, minAutoSize: number, maxAutoSize: number }[], minColumnWidth: number, autoClearance: number): void {
    var $inner = $table.parent();

    var scrollLeftBefore = $inner.scrollLeft();
    var scrollTopBefore = $inner.scrollTop();

    applyColumnWidths($table, columnMetadata, minColumnWidth, autoClearance);
    resizeInternal($table);

    $inner.scrollLeft(scrollLeftBefore);
    $inner.scrollTop(scrollTopBefore);

    updateHeadingPositions($table);
}

export function constructTable(
    columnMetadata: BehaviorSubject<ColumnInfo[]>,
    data: Observable<any>,
    keyLambda: (any) => any,
    externalResize: Observable<any>,
    minColumnWidth: number,
    columnPadding: number,
    applyUnobtrusiveScrolling = false,
    rowFactory: (item: any, metas: ColumnInfo[], keyLambda: (x: any) => any) => HTMLTableRowElement = null
) {
    var $tableContainer = $('<div />').addClass('rxui-table');
    var $outer = $('<div />').appendTo($tableContainer);
    var $headerBackground = $('<div />').addClass('rxui-table-header-background').appendTo($outer);
    var $inner = $('<div />').addClass('rxui-table-inner').appendTo($outer);
    var $table = $('<table />').addClass('rxui-table-fixed').appendTo($inner);
    var $tbody = $('<tbody />').appendTo($table);

    buildHeader($table, columnMetadata.getValue());

    var columnResized = makeColumnsResizable($table).share();

    var updateRequired = Observable.of({}).merge(externalResize).merge(columnResized);

    var subscriptions = disposableSubscription(
        rxui.scrolled($inner).switchMap(() => Observable.timer(0)).subscribe(() => updateHeadingPositions($table)),

        columnResized.subscribe(x => {
            var column = columnMetadata.getValue()[x.index];
            column.sizeType = 'pixel';
            column.size = Math.max(x.size, minColumnWidth);
        }),

        updateRequired.subscribe(() => window.setTimeout(() => resize($table, columnMetadata.getValue(), minColumnWidth, columnPadding))),

        data.subscribe(x => {
            populateTable($table, columnMetadata.getValue(), x, keyLambda, null, rowFactory);
            resize($table, columnMetadata.getValue(), minColumnWidth, columnPadding);
        }),

        makeHeadersDraggable($table).subscribe(x => {
            var metadata = columnMetadata.getValue();
            moveColumn($table, x.oldIndex, x.newIndex, metadata.length);

            var arr = metadata.slice(0);
            arr.splice(x.newIndex, 0, arr.splice(x.oldIndex, 1)[0]);
            columnMetadata.next(arr);
        })
    );

    $tableContainer.on('remove', () => subscriptions.dispose());

    createChock().appendTo($table);

    if (applyUnobtrusiveScrolling)
        unobtrusiveScroll($inner, 'vertical', data.merge(externalResize).startWith({}).delay(0), $table, 25);

    return { $tableContainer, $table };
}

function jqToSeq(jq: JQuery) {
    return Seq(jq.toArray()).map(x => $(x));
}